import {
  LAST_PRUNED_STORAGE_KEY,
  READ_ITEMS_STORAGE_KEY,
  UNREAD_WINDOW_DAYS,
} from '../constants';

export interface ReadEntry {
  id: string;
  firstPublishedAt: string; // ISO 8601
}

/** Canonical YYYY-MM-DD date key in the user's local timezone. */
type LocalDateKey = string;

const getStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const readStorageItem = (key: string): string | null => {
  try {
    return getStorage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const writeStorageItem = (key: string, value: string): void => {
  try {
    getStorage()?.setItem(key, value);
  } catch {
    // localStorage may be unavailable or full; read state should never break UI.
  }
};

export const toLocalDateKey = (date: Date): LocalDateKey => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

/**
 * Convert a backend-supplied ISO 8601 timestamp into a canonical YYYY-MM-DD key
 * in the user's local timezone, so the unread window matches the calendar the
 * user actually sees.
 *
 * Returns null if the timestamp is not parseable.
 */
const parsePostDateKey = (postDate: string): LocalDateKey | null => {
  const date = new Date(postDate);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return toLocalDateKey(date);
};

const getUnreadBoundaryKey = (): LocalDateKey => {
  const today = new Date();

  return toLocalDateKey(
    new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() - UNREAD_WINDOW_DAYS,
    ),
  );
};

/**
 * Whether an ISO 8601 timestamp falls within [today - UNREAD_WINDOW_DAYS, today]
 * in local time. False for unparsable dates.
 */
export const isWithinUnreadWindow = (firstPublishedAt: string): boolean => {
  const firstPublishedAtKey = parsePostDateKey(firstPublishedAt);

  if (firstPublishedAtKey === null) {
    return false;
  }

  return (
    firstPublishedAtKey >= getUnreadBoundaryKey() &&
    firstPublishedAtKey <= toLocalDateKey(new Date())
  );
};

const isReadEntry = (value: unknown): value is ReadEntry =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as { id?: unknown }).id === 'string' &&
  typeof (value as { firstPublishedAt?: unknown }).firstPublishedAt ===
    'string';

// Cache the parsed ids so the snapshot returns a stable reference until the raw
// string changes — required by useSyncExternalStore and keeps tearing protection.
let cachedRawValue: string | null = null;
let cachedReadIds: string[] = [];

export const getReadIdsSnapshot = (): string[] => {
  const rawValue = readStorageItem(READ_ITEMS_STORAGE_KEY);

  if (rawValue !== cachedRawValue) {
    cachedRawValue = rawValue;
    cachedReadIds = getReadIds();
  }

  return cachedReadIds;
};

/**
 * Read stored entries, dropping invalid ones and de-duplicating by id (first
 * wins).
 */
export const getReadEntries = (): ReadEntry[] => {
  const storedValue = readStorageItem(READ_ITEMS_STORAGE_KEY);

  if (storedValue === null) {
    return [];
  }

  try {
    const parsedValue: unknown = JSON.parse(storedValue);

    if (!Array.isArray(parsedValue)) {
      return [];
    }

    const seenIds = new Set<string>();
    const entries: ReadEntry[] = [];

    parsedValue.forEach((item) => {
      if (!isReadEntry(item) || seenIds.has(item.id)) {
        return;
      }

      seenIds.add(item.id);
      entries.push({ id: item.id, firstPublishedAt: item.firstPublishedAt });
    });

    return entries;
  } catch {
    return [];
  }
};

export const getReadIds = (): string[] =>
  getReadEntries().map((entry) => entry.id);

export const addReadEntry = ({ id, firstPublishedAt }: ReadEntry): void => {
  const normalizedId = id.trim();

  // Don't persist invalid entries — a ReadEntry must have a usable date.
  if (
    normalizedId.length === 0 ||
    parsePostDateKey(firstPublishedAt) === null
  ) {
    return;
  }

  const entries = getReadEntries();

  if (entries.some((entry) => entry.id === normalizedId)) {
    return;
  }

  writeStorageItem(
    READ_ITEMS_STORAGE_KEY,
    JSON.stringify([...entries, { id: normalizedId, firstPublishedAt }]),
  );

  // The browser does not fire the 'storage' event for same-window writes, so
  // dispatch our own custom event to notify same-tab subscribers.
  try {
    window.dispatchEvent(new Event('unread-indicator-changed'));
  } catch {
    // window may not be available in SSR — safe to skip.
  }
};

/**
 * Drop stored read entries that have aged out of the unread window.
 *
 * Each entry carries its own firstPublishedAt, so pruning needs no external
 * data. An entry is kept only if its date parses and falls within
 * [today - UNREAD_WINDOW_DAYS, today] in the user's local calendar.
 *
 * Self-throttles to at most once per local calendar day via
 * LAST_PRUNED_STORAGE_KEY, so callers can invoke it on every app load.
 */
export const pruneReadIds = (): void => {
  const todayKey = toLocalDateKey(new Date());

  // Short-circuit: already pruned today (cheap YYYY-MM-DD string compare).
  if (readStorageItem(LAST_PRUNED_STORAGE_KEY) === todayKey) {
    return;
  }

  const prunedEntries = getReadEntries().filter((entry) =>
    isWithinUnreadWindow(entry.firstPublishedAt),
  );

  writeStorageItem(READ_ITEMS_STORAGE_KEY, JSON.stringify(prunedEntries));
  writeStorageItem(LAST_PRUNED_STORAGE_KEY, todayKey);
};
